import { type ReactNode, useId } from 'react';
import { Label } from './label';
import { cn } from '@/lib/cn';

type FormFieldRenderProps = {
  id: string;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
  'aria-required'?: boolean;
};

type FormFieldProps = {
  id?: string;
  label: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  className?: string;
  /**
   * Children receive the field's id + aria attrs so they can be spread
   * onto the underlying input/select/textarea. The Label is rendered
   * automatically with `htmlFor` set to the same id.
   */
  children: (props: FormFieldRenderProps) => ReactNode;
};

/**
 * Form field wrapper that renders a label + hint + error with full ARIA
 * wiring. Children receive `id`, `aria-invalid`, and `aria-describedby`
 * to spread on the underlying input/select/textarea.
 *
 * The error message is announced via `role="alert" aria-live="polite"`.
 */
export const FormField = ({
  id,
  label,
  hint,
  error,
  required,
  className,
  children,
}: FormFieldProps) => {
  const reactId = useId();
  const fieldId = id ?? `field-${reactId}`;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={fieldId} className="flex items-center gap-1">
        {label}
        {required && (
          <>
            <span aria-hidden="true" className="text-danger">
              *
            </span>
            <span className="sr-only">required</span>
          </>
        )}
      </Label>
      {hint && (
        <p id={hintId} className="text-xs text-text-secondary">
          {hint}
        </p>
      )}
      {children({ id: fieldId, 'aria-invalid': invalid, 'aria-describedby': describedBy, 'aria-required': required })}
      {error && (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="text-xs text-danger"
        >
          {error}
        </p>
      )}
    </div>
  );
};
